/**
 * CONFIRM DIALOG - Apple Liquid Glass Design System
 * macOS Tahoe 26.2 & iOS 26.2
 * Based on WWDC25: Meet Liquid Glass
 *
 * Features:
 * - Liquid Glass Thick material for maximum visual impact
 * - "Pop open" animation from bubble
 * - Destructive action styling with red tinting
 * - Keyboard navigation (Enter/Escape)
 * - Focus trap for accessibility
 * - Animated icon based on variant
 */

import { motion, AnimatePresence } from 'framer-motion'
import { AlertTriangle, AlertCircle, Info, CheckCircle2, HelpCircle, Trash2 } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { Button } from './Button'
import { cn } from '../../lib/utils'

type DialogVariant = 'danger' | 'warning' | 'info' | 'success' | 'question' | 'delete'

interface ConfirmDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  message: string
  confirmText?: string
  cancelText?: string
  variant?: DialogVariant
  isLoading?: boolean
  showCancel?: boolean
}

const variantConfig = {
  danger: {
    icon: AlertCircle,
    iconColor: 'text-red dark:text-red-dark',
    iconBg: 'bg-red-glass dark:bg-red-glass',
    buttonVariant: 'destructive' as const
  },
  warning: {
    icon: AlertTriangle,
    iconColor: 'text-orange dark:text-orange-dark',
    iconBg: 'bg-orange-glass dark:bg-orange-glass',
    buttonVariant: 'primary' as const
  },
  info: {
    icon: Info,
    iconColor: 'text-blue dark:text-blue-dark',
    iconBg: 'bg-blue-glass dark:bg-blue-glass',
    buttonVariant: 'primary' as const
  },
  success: {
    icon: CheckCircle2,
    iconColor: 'text-green dark:text-green-dark',
    iconBg: 'bg-green-glass dark:bg-green-glass',
    buttonVariant: 'primary' as const
  },
  question: {
    icon: HelpCircle,
    iconColor: 'text-purple dark:text-purple-dark',
    iconBg: 'bg-purple-glass dark:bg-purple-glass',
    buttonVariant: 'primary' as const
  },
  delete: {
    icon: Trash2,
    iconColor: 'text-red dark:text-red-dark',
    iconBg: 'bg-red-glass dark:bg-red-glass',
    buttonVariant: 'destructive' as const
  }
}

export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger',
  isLoading = false,
  showCancel = true
}: ConfirmDialogProps) {
  const confirmButtonRef = useRef<HTMLButtonElement>(null)
  const cancelButtonRef = useRef<HTMLButtonElement>(null)

  const config = variantConfig[variant]
  const Icon = config.icon

  // Prevent scroll when dialog is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  // Focus management
  useEffect(() => {
    if (isOpen && showCancel && cancelButtonRef.current) {
      cancelButtonRef.current.focus()
    } else if (isOpen && confirmButtonRef.current) {
      confirmButtonRef.current.focus()
    }
  }, [isOpen, showCancel])

  // Keyboard handling
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen || isLoading) return

      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      } else if (e.key === 'Enter') {
        e.preventDefault()
        onConfirm()
      } else if (e.key === 'Tab') {
        // Focus trap
        const focusableElements = [cancelButtonRef.current, confirmButtonRef.current].filter(Boolean)
        const firstElement = focusableElements[0]
        const lastElement = focusableElements[focusableElements.length - 1]

        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault()
          lastElement?.focus()
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault()
          firstElement?.focus()
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose, onConfirm, isLoading])

  // Liquid Glass spring animation config
  const springConfig = {
    type: "spring",
    damping: 25,
    stiffness: 300,
    mass: 0.8
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Dimming Backdrop with Glass Blur */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-0 z-[60] liquid-modal-backdrop"
            onClick={!isLoading ? onClose : undefined}
            aria-hidden="true"
          />

          {/* Dialog Container */}
          <div
            className="fixed inset-0 z-[70] flex items-center justify-center p-4"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="dialog-title"
            aria-describedby="dialog-description"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 30 }}
              transition={springConfig}
              className={cn(
                'w-full max-w-md',
                'liquid-material-thick liquid-lens',
                'rounded-3xl overflow-hidden',
                'shadow-liquid-xl dark:shadow-dark-liquid-xl',
                'border border-black/[0.06] dark:border-white/[0.08]'
              )}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Specular highlight */}
              <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-white/20 to-transparent dark:from-white/10 pointer-events-none" />

              {/* Content */}
              <div className="relative p-6">
                {/* Animated Icon */}
                <motion.div
                  className="flex justify-center mb-5"
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ ...springConfig, delay: 0.1 }}
                >
                  <div className={cn(
                    'w-16 h-16 rounded-2xl flex items-center justify-center',
                    'shadow-glass dark:shadow-dark-glass',
                    config.iconBg
                  )}>
                    <Icon className={cn('w-8 h-8', config.iconColor)} strokeWidth={2} />
                  </div>
                </motion.div>

                {/* Title */}
                <motion.h3
                  id="dialog-title"
                  className="text-title2 font-bold text-light-text-primary dark:text-dark-text-primary text-center mb-2 tracking-tight"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 }}
                >
                  {title}
                </motion.h3>

                {/* Message */}
                <motion.p
                  id="dialog-description"
                  className="text-body text-light-text-secondary dark:text-dark-text-secondary text-center mb-6 tracking-tight"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  {message}
                </motion.p>

                {/* Actions */}
                <motion.div
                  className={cn(
                    'flex gap-3',
                    !showCancel && 'justify-center'
                  )}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 }}
                >
                  {showCancel && (
                    <Button
                      ref={cancelButtonRef}
                      variant="glass"
                      fullWidth
                      onClick={onClose}
                      disabled={isLoading}
                    >
                      {cancelText}
                    </Button>
                  )}
                  <Button
                    ref={confirmButtonRef}
                    variant={config.buttonVariant}
                    fullWidth={showCancel}
                    onClick={onConfirm}
                    isLoading={isLoading}
                  >
                    {confirmText}
                  </Button>
                </motion.div>
              </div>

              {/* Bottom edge highlight */}
              <div className="absolute bottom-0 left-4 right-4 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent dark:via-white/10" />
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  )
}

/**
 * AlertDialog - Simpler alert with just an OK button
 */
interface AlertDialogProps {
  isOpen: boolean
  onClose: () => void
  title: string
  message: string
  buttonText?: string
  variant?: DialogVariant
}

export function AlertDialog({
  isOpen,
  onClose,
  title,
  message,
  buttonText = 'OK',
  variant = 'info'
}: AlertDialogProps) {
  return (
    <ConfirmDialog
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={onClose}
      title={title}
      message={message}
      confirmText={buttonText}
      variant={variant}
      showCancel={false}
    />
  )
}

/**
 * DeleteConfirmDialog - Pre-configured for delete actions
 */
interface DeleteConfirmDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  itemName?: string
  isLoading?: boolean
}

export function DeleteConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  itemName = 'this item',
  isLoading = false
}: DeleteConfirmDialogProps) {
  return (
    <ConfirmDialog
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={onConfirm}
      title="Delete Item"
      message={`Are you sure you want to delete ${itemName}? This action cannot be undone.`}
      confirmText="Delete"
      cancelText="Cancel"
      variant="delete"
      isLoading={isLoading}
    />
  )
}
